<template>
  <div
      class="cws-container cws-sidebar-right blog-wrapper"
  >

    <!-- content -->
    <div class="blog-detail-wrapper">
      <b-row>
        <b-col cols="12">
          <b-card>
            <b-overlay
                :show="isLoadingInfo"
                rounded="sm"
            >
            <!-- Track info -->
            <div v-if="trackInfo">
              <h4>
                Трек
                <b-button
                    v-if="trackInfo.artist_id"
                    variant="flat-primary"
                    size="sm"
                    class="ml-50"
                    :to="{name: 'artist-view', params: { id: trackInfo.artist_id }}"
                >
                  {{ trackInfo.artist }}
                  <feather-icon icon="ExternalLinkIcon" size="14" class="ml-25"/>
                </b-button>
              </h4>
              <b-card class="mb-2" border-variant="primary">
                <h5 class="d-flex align-items-center">
                  <b-button
                      v-if="trackInfo.file"
                      variant="success"
                      class="btn-icon mr-1"
                      size="sm"
                      :href="trackInfo.url"
                  >
                    <feather-icon icon="SaveIcon"/>
                  </b-button>
                  <b-button
                      v-else
                      variant="flat-danger"
                      class="btn-icon mr-1"
                      size="sm"
                      disabled
                  >
                    <feather-icon icon="AlertCircleIcon"/>
                  </b-button>
                  <template v-if="!isEditingTitle">
                    {{ trackInfo.artist }} — {{ trackInfo.track }}
                    <b-button
                        variant="flat-primary"
                        class="btn-icon ml-50"
                        size="sm"
                        @click="startEditingTitle"
                    >
                      <feather-icon icon="Edit2Icon"/>
                    </b-button>
                  </template>
                  <template v-else>
                    <b-form-input
                        v-model="editTitle"
                        class="mr-1"
                        style="max-width: 400px;"
                        @keyup.enter="saveTitle"
                    />
                    <b-button
                        variant="success"
                        class="btn-icon mr-50"
                        size="sm"
                        @click="saveTitle"
                    >
                      <feather-icon icon="CheckIcon"/>
                    </b-button>
                    <b-button
                        variant="flat-secondary"
                        class="btn-icon"
                        size="sm"
                        @click="cancelEditingTitle"
                    >
                      <feather-icon icon="XIcon"/>
                    </b-button>
                  </template>
                </h5>
                <div class="d-flex align-items-center">
                  <span class="mr-2">Прослушивания: {{ formatNumber(trackInfo.play_count) }}</span>
                  <span v-if="trackInfo.release_year" class="mr-2">Год выпуска: {{ trackInfo.release_year }}</span>
                </div>
                <div class="mt-50 d-flex align-items-center">
                  <small class="text-muted mr-50">Внешние ID:</small>
                  <template v-if="trackInfo.external_ids">
                    <span v-if="trackInfo.external_ids.spotify" class="d-inline-flex align-items-center mr-25">
                      <b-badge
                          v-b-tooltip.hover
                          :title="'Spotify: ' + trackInfo.external_ids.spotify"
                          tag="a"
                          :href="'https://open.spotify.com/track/' + trackInfo.external_ids.spotify"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="success"
                          class="cursor-pointer"
                          style="font-size: 0.7rem; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                      >SP</b-badge>
                      <b-button
                          variant="flat-danger"
                          class="btn-icon p-0"
                          size="sm"
                          style="line-height: 1; margin-left: -2px;"
                          @click="deleteExternalId('spotify')"
                      >
                        <feather-icon icon="XIcon" size="11" />
                      </b-button>
                    </span>
                    <span v-if="trackInfo.external_ids.yandex" class="d-inline-flex align-items-center mr-25">
                      <b-badge
                          v-b-tooltip.hover
                          :title="'Yandex Music: ' + trackInfo.external_ids.yandex"
                          tag="a"
                          :href="'https://music.yandex.ru/track/' + trackInfo.external_ids.yandex"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="danger"
                          class="cursor-pointer"
                          style="font-size: 0.7rem; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                      >YA</b-badge>
                      <b-button
                          variant="flat-danger"
                          class="btn-icon p-0"
                          size="sm"
                          style="line-height: 1; margin-left: -2px;"
                          @click="deleteExternalId('yandex')"
                      >
                        <feather-icon icon="XIcon" size="11" />
                      </b-button>
                    </span>
                    <span v-if="trackInfo.external_ids.youtube" class="d-inline-flex align-items-center mr-25">
                      <b-badge
                          v-b-tooltip.hover
                          :title="'YouTube: ' + trackInfo.external_ids.youtube"
                          tag="a"
                          :href="'https://www.youtube.com/watch?v=' + trackInfo.external_ids.youtube"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="danger"
                          class="cursor-pointer"
                          style="font-size: 0.7rem; background-color: #ff0000 !important; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                      >YT</b-badge>
                      <b-button
                          variant="flat-danger"
                          class="btn-icon p-0"
                          size="sm"
                          style="line-height: 1; margin-left: -2px;"
                          @click="deleteExternalId('youtube')"
                      >
                        <feather-icon icon="XIcon" size="11" />
                      </b-button>
                    </span>
                    <span v-if="trackInfo.external_ids.second_hand_songs" class="d-inline-flex align-items-center mr-25">
                      <b-badge
                          v-b-tooltip.hover
                          :title="'SHS: ' + trackInfo.external_ids.second_hand_songs"
                          tag="a"
                          :href="'https://secondhandsongs.com/performance/' + trackInfo.external_ids.second_hand_songs"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="info"
                          class="cursor-pointer"
                          style="font-size: 0.7rem; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                      >SH</b-badge>
                      <b-button
                          variant="flat-danger"
                          class="btn-icon p-0"
                          size="sm"
                          style="line-height: 1; margin-left: -2px;"
                          @click="deleteExternalId('second_hand_songs')"
                      >
                        <feather-icon icon="XIcon" size="11" />
                      </b-button>
                    </span>
                  </template>
                  <b-button
                      v-b-tooltip.hover
                      title="Добавить внешний ID"
                      variant="flat-primary"
                      class="btn-icon p-25"
                      size="sm"
                      @click="showAddExternalIdModal = true"
                  >
                    <feather-icon icon="PlusCircleIcon" size="14"/>
                  </b-button>
                </div>

                <!-- Modal: Добавить внешний ID -->
                <b-modal
                    v-model="showAddExternalIdModal"
                    title="Добавить внешний ID"
                    ok-title="Добавить"
                    cancel-title="Отмена"
                    centered
                    :ok-disabled="!externalIdInput.trim() || isAddingExternalId"
                    :busy="isAddingExternalId"
                    @ok.prevent="addExternalId"
                    @hidden="externalIdInput = ''"
                >
                  <b-form-group
                      label="Ссылка или идентификатор"
                      description="Вставьте ссылку на трек (Spotify, YouTube, Yandex Music, SHS) или идентификатор"
                  >
                    <b-form-input
                        v-model="externalIdInput"
                        placeholder="https://open.spotify.com/track/... или ID"
                        autofocus
                        @keyup.enter="addExternalId"
                    />
                  </b-form-group>
                </b-modal>
              </b-card>

              <!-- Audio player -->
              <div v-if="trackInfo.file" class="mb-2">
                <audio
                    controls
                    :src="trackInfo.file_url"
                    style="width: 100%;"
                    preload="none"
                />
              </div>
            </div>
            </b-overlay>

            <!-- Tabs -->
            <b-tabs v-model="activeTabIndex" content-class="mt-1">
              <b-tab title="Скачать">
                <track-detail-search
                    :track-info="trackInfo"
                    @track-updated="fetchTrackInfo"
                />
              </b-tab>
              <b-tab title="Каверы">
                <track-detail-covers
                    :track-id="trackId"
                />
              </b-tab>
            </b-tabs>

          </b-card>
        </b-col>
      </b-row>
    </div>
    <!--/ content -->

  </div>
</template>

<script>
import {
  BFormInput,
  BFormGroup,
  BCard,
  BRow,
  BCol,
  BBadge,
  BButton,
  BTab,
  BTabs,
  BModal,
  BOverlay,
  VBTooltip,
} from 'bootstrap-vue'
import {formatNumber} from '@core/utils/filter'
import router from "@/router"
import store from "@/store"
import musicStoreModule from "@/views/db-music/musicStoreModule"
import {computed, onUnmounted, ref, watch} from "@vue/composition-api"
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TrackDetailSearch from './TrackDetailSearch.vue'
import TrackDetailCovers from './TrackDetailCovers.vue'

export default {
  components: {
    BFormInput,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BBadge,
    BButton,
    BTab,
    BTabs,
    BModal,
    BOverlay,
    TrackDetailSearch,
    TrackDetailCovers,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const STORE_MODULE_NAME = 'artists'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const trackInfo = ref(null)
    const isLoadingInfo = ref(false)
    const isEditingTitle = ref(false)
    const editTitle = ref('')
    const showAddExternalIdModal = ref(false)
    const externalIdInput = ref('')
    const isAddingExternalId = ref(false)

    const trackId = computed(() => router.currentRoute.params.id)

    // Tab from query
    const tabMap = { search: 0, covers: 1 }
    const initialTab = tabMap[router.currentRoute.query.tab] || 0
    const activeTabIndex = ref(initialTab)

    const fetchTrackInfo = async () => {
      isLoadingInfo.value = true
      try {
        const res = await store.dispatch('artists/fetchTrackDetails', {id: trackId.value})
        trackInfo.value = res.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки данных трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingInfo.value = false
      }
    }

    const startEditingTitle = () => {
      editTitle.value = trackInfo.value.title
      isEditingTitle.value = true
    }

    const cancelEditingTitle = () => {
      isEditingTitle.value = false
    }

    const saveTitle = async () => {
      const newTitle = editTitle.value.trim()
      if (!newTitle || newTitle === trackInfo.value.title) {
        isEditingTitle.value = false
        return
      }
      isLoadingInfo.value = true
      try {
        await store.dispatch('artists/updateTrack', {id: trackInfo.value.id, queryParams: {title: newTitle}})
        await fetchTrackInfo()
        isEditingTitle.value = false
        toast({
          component: ToastificationContent,
          props: {
            title: 'Название обновлено',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления названия',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingInfo.value = false
      }
    }

    const addExternalId = async () => {
      const url = externalIdInput.value.trim()
      if (!url) return
      isAddingExternalId.value = true
      try {
        const res = await store.dispatch('artists/addTrackExternalId', {id: trackId.value, url})
        showAddExternalIdModal.value = false
        externalIdInput.value = ''
        trackInfo.value.external_ids = res.data.external_ids
        toast({
          component: ToastificationContent,
          props: {
            title: 'Внешний ID добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка добавления внешнего ID'
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isAddingExternalId.value = false
      }
    }

    const deleteExternalId = async (source) => {
      isLoadingInfo.value = true
      try {
        const res = await store.dispatch('artists/deleteTrackExternalId', {id: trackId.value, source})
        trackInfo.value.external_ids = res.data.external_ids
        toast({
          component: ToastificationContent,
          props: {
            title: 'Внешний ID удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка удаления внешнего ID'
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingInfo.value = false
      }
    }

    fetchTrackInfo()

    return {
      trackInfo,
      trackId,
      isLoadingInfo,
      isEditingTitle,
      editTitle,
      activeTabIndex,
      formatNumber,
      fetchTrackInfo,
      startEditingTitle,
      cancelEditingTitle,
      saveTitle,
      showAddExternalIdModal,
      externalIdInput,
      isAddingExternalId,
      addExternalId,
      deleteExternalId,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
